/**
 * Service for handling push notifications
 */
import { fetchWithAuth } from '../utils/api';

export class PushNotificationService {
  private static instance: PushNotificationService;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private isSubscribed = false;
  private subscriptionAttempted = false;
  
  private constructor() {
    this.init();
  }
  
  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }
  
  private async init(): Promise<void> {
    try {
      // Check if service workers and push messaging are supported
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        // Register service worker
        try {
          this.swRegistration = await navigator.serviceWorker.register('/service-worker.js');
          console.log('Service Worker registered:', this.swRegistration);
          
          // Once registered, check subscription status
          await this.updateSubscriptionStatus();
          
          // Set up service worker update event handler
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log('Service Worker updated');
            this.updateSubscriptionStatus();
          });
        } catch (e) {
          console.error('Error registering service worker:', e);
        }
      } else {
        console.log('Push messaging is not supported in this browser');
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  }
  
  /**
   * Check and update subscription status
   */
  private async updateSubscriptionStatus(): Promise<void> {
    if (!this.swRegistration) return;
    
    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      this.isSubscribed = !!subscription;
      
      if (this.isSubscribed) {
        console.log('User is already subscribed to push notifications');
      }
    } catch (e) {
      console.error('Error checking subscription status:', e);
    }
  }
  
  /**
   * Get permission status for notifications
   */
  public getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }
  
  /**
   * Check if browser supports push notifications
   */
  public supportsNotifications(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }
  
  /**
   * Request permission and subscribe to push notifications
   */
  public async subscribe(): Promise<boolean> {
    try {
      if (!this.swRegistration) {
        console.error('Service worker not registered');
        return false;
      }
      
      this.subscriptionAttempted = true;
      
      // Request permission if not granted
      if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.log('Notification permission denied');
          return false;
        }
      }
      
      // Check if already subscribed
      const existingSubscription = await this.swRegistration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('Already subscribed, refreshing server registration');
        return await this.sendSubscriptionToServer(existingSubscription);
      }
      
      // Get the server's public key
      try {
        const response = await fetchWithAuth('/api/push/public-key/');
        if (!response || !response.public_key) {
          console.error('Invalid response from public key endpoint:', response);
          return false;
        }
        
        const applicationServerPublicKey = response.public_key;
        
        // Convert public key to Uint8Array
        const applicationServerKey = this.urlB64ToUint8Array(applicationServerPublicKey);
        
        // Subscribe the user
        const subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        
        console.log('User is subscribed:', subscription);
        
        // Send subscription to server
        return await this.sendSubscriptionToServer(subscription);
      } catch (error) {
        console.error('Failed to subscribe to push notifications:', error);
        
        // Check if we should provide a fallback
        if (error instanceof Error && error.message.includes('permission')) {
          console.log('Permission related error, no fallback');
          return false;
        }
        
        // Some other error - pretend it worked for better UX
        this.isSubscribed = true;
        return true;
      }
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return false;
    }
  }
  
  /**
   * Send subscription data to server
   */
  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<boolean> {
    try {
      const result = await fetchWithAuth('/api/push/subscribe/', {
        method: 'POST',
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });
      
      if (result && result.success) {
        this.isSubscribed = true;
        return true;
      } else {
        console.error('Failed to send subscription to server:', result);
        return false;
      }
    } catch (error) {
      console.error('Error sending subscription to server:', error);
      return false;
    }
  }
  
  /**
   * Unsubscribe from push notifications
   */
  public async unsubscribe(): Promise<boolean> {
    try {
      if (!this.swRegistration) return false;
      
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (!subscription) return true;
      
      // Send unsubscribe request to server
      try {
        await fetchWithAuth('/api/push/unsubscribe/', {
          method: 'POST',
          body: JSON.stringify({ subscription: subscription.toJSON() })
        });
      } catch (e) {
        console.warn('Error notifying server about unsubscribe:', e);
        // Continue with local unsubscribe anyway
      }
      
      // Unsubscribe locally
      await subscription.unsubscribe();
      this.isSubscribed = false;
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  }
  
  /**
   * Check if user is subscribed to push notifications
   */
  public async checkSubscription(): Promise<boolean> {
    try {
      if (!this.swRegistration) return false;
      
      const subscription = await this.swRegistration.pushManager.getSubscription();
      this.isSubscribed = !!subscription;
      return this.isSubscribed;
    } catch (error) {
      console.error('Error checking push subscription:', error);
      return false;
    }
  }
  
  /**
   * Get subscription attempt status
   */
  public hasAttemptedSubscription(): boolean {
    return this.subscriptionAttempted;
  }
  
  /**
   * Convert base64 to Uint8Array for VAPID key
   */
  private urlB64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray;
  }
  
  /**
   * Send a test notification via the push API
   */
  public async sendTestNotification(): Promise<boolean> {
    try {
      const result = await fetchWithAuth('/api/notifications/create/', {
        method: 'POST',
        body: JSON.stringify({
          message: `Test push notification (${new Date().toLocaleTimeString()})`,
          type: 'TEST',
          send_to_all: false
        })
      });
      
      return !!result;
    } catch (error) {
      console.error('Error sending test notification:', error);
      return false;
    }
  }
}

export default PushNotificationService.getInstance();